import React, { useState, useEffect, useRef, useMemo } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { createSelector } from "reselect";
import { useIntl, FormattedNumber } from "react-intl";
import moment from "moment";

//Helpers
import {
    formatOffsetDisplayOdometer,
    formatOffsetDisplayEngineHours, convertToKPH,
} from "Core/data/Helpers";
import { GetFormatForMoment } from '../../../../core/hooks/utils'
import { usePaggination } from '../../../../core/hooks'
//Components
import {
    Container,
    TableView,
    Breadcrumb,
    MenuComponent,
    Icons,
    SearchInput,
    TableButtons,
    IconButton,
    Pill
} from "@gpstrackit/gtc-react-controls";
import { Grid } from '@mui/material';


//Views
import CompleteMaintenanceService from '../../modals/completeMaintenanceService';
import DeleteMaintenanceService from '../../modals/deleteMaintenanceServiceUnit';
import ScheduleMaintenanceService from '../../modals/scheduleMaintenanceService';
import EntityDetails from '../../modals/entityDetails';

//Hooks
import { getMaintenanceBar, getMaintenanceLabel, getMaintenanceValue } from '../../../../core/hooks';

//Actions
import {
    maintenanceVSDGetMaintenanceService,
    maintenanceVSDSearchUnits,
    reset,
} from './redux/slice';
import {
    openModalScheduleMaintenanceServiceUnit,
    openModalDeleteMaintenanceServiceUnit,
    openModalCompleteMaintenance,
    openModalEntityDetails,
} from 'Redux/actions';

//Styles
import styles from "Assets/sass/core/vars.scss";
import './styles.scss';

import { useDataGridThrottlerWithTrackedIds } from 'Core/hooks'
import { unitSelectors } from 'Core/realtime/redux/unitSlice';
import { lastReadingSelectors } from 'Core/realtime/redux/lastReadingSlice';
import { getUnitsNaturalOrder } from 'Core/data/Helpers';

const getUnitsCatalog = createSelector([state => unitSelectors.selectAll(state)], getUnitsNaturalOrder);
const getReadingsCatalog = (state) => lastReadingSelectors.selectEntities(state);

const getData =
    createSelector([getUnitsCatalog, getReadingsCatalog], (units, readings) => {
        let data = [];
        units.map((unit) => {
            if (unit.show != undefined && unit.show === 0) {
                return;
            }
            let lastReading = readings[unit.id]

            let dataToPush = {
                ...unit,
                engineOdometer: lastReading?.engineOdometer,
                ecuEngineOdometer: lastReading?.ecuEngineOdometer,
                engineHours: lastReading?.engineHours,
                ecuEngineHours: lastReading?.ecuEngineHours,
            };
            dataToPush.displayOdometer = formatOffsetDisplayOdometer(dataToPush);
            dataToPush.displayHours = formatOffsetDisplayEngineHours(dataToPush);
            data.push(dataToPush);
        });
        return data;
    })

const ServiceDetailView = (props) => {
    const { messages, locale, formatNumber } = useIntl();
    const dispatch = useDispatch();
    const location = useLocation();
    const navigate = useNavigate();
    const tableRef = useRef();

    const pagination = usePaggination(null, null, true, tableRef);

    //Redux States
    const { serviceDetail, vehicles, loadingServiceDetail, loadingVehicles } = useSelector((state) => state.maintenanceServiceDetailRedux);
    const openDeleteMaintenanceServiceUnit = useSelector(state => state.maintenanceDeleteMaintenanceServiceUnitRedux.open);
    const openScheduleMaintenanceService = useSelector(state => state.maintenanceScheduleMaintenanceServiceRedux.open);
    const openCompleteMaintenanceService = useSelector(state => state.maintenanceCompleteMaintenanceServiceRedux.open);
    const openEntityDetails = useSelector(state => state.maintenanceEntityDetailsRedux.open);
    const { user } = useSelector((state) => state.securityRedux);

    //Local States
    const [currentVehicle, setCurrentVehicle] = useState(false);
    const [vehicleList, setVehicleList] = useState([]);

    let path = location.pathname.split("/");
    const id = path[path.length - 1];

    const menuServiceDetailOptions = [
        messages["complete"],
        messages["edit"],
        messages["delete"],
    ];

    //Create a computed data from catalog
    const computedData = useSelector(state => getData(state) || []);
    const propertiesToValidate = useMemo(() => ["label",
        "engineOdometer",
        "engineHours"], []);

    // Hook handles both Throttled Arrays for `<Map/>` and imperative delta diffing for markers via `handleMapUpdates`
    const [data, changeIdsCallback] = useDataGridThrottlerWithTrackedIds(
        computedData,
        1000,
        () => { },
        "id",
        propertiesToValidate
    );

    useEffect(() => {
        dispatch(maintenanceVSDGetMaintenanceService({ id }));
        dispatch(maintenanceVSDSearchUnits({}));

        return () => dispatch(reset());
    }, []);


    const handleDetails = (entityId, entityName, numberTab, title) => {
        dispatch(openModalEntityDetails({ open: true, entityId, entityName, numberTab, title }))
    };

    const refreshTable = () => {
        dispatch(maintenanceVSDGetMaintenanceService({ id }));
    }

    const getVehicleById = (id) => {
        const vehicle = vehicleList.find((unit) => {
            return unit.id && unit.id.toString() === id.toString();
        });
        return vehicle;
    };

    useEffect(() => {

        pagination.setColumns([
            {
                key: "title",
                width: 200,
                sort: true,
                show: true,
                title: messages['serviceDetailView_vehicle'],
                export: true,
                render: (row) => {
                    const vehicle = row?.vehicle;
                    return (
                        <Grid container alignItems="center" justifyContent="space-between">
                            <Grid item xs={9} style={{ width: 200 }}>
                                <span className="table-span">{vehicle && vehicle.label}</span>
                            </Grid>
                            <Grid item xs={3}>
                                <MenuComponent
                                    disabled={loadingServiceDetail}
                                    options={menuServiceDetailOptions}
                                    icon="more_vert"
                                    className="showInHover"
                                    onClick={(type, serviceUnit) => {
                                        if (type === messages["complete"]) {
                                            dispatch(openModalCompleteMaintenance({ open: true, serviceUnitId: serviceUnit.id }))
                                        }
                                        if (type === messages["edit"]) {
                                            dispatch(openModalScheduleMaintenanceServiceUnit({ open: true, id: serviceUnit.id }))
                                        }
                                        if (type === messages["delete"]) {
                                            dispatch(openModalDeleteMaintenanceServiceUnit({ open: true, service: serviceUnit }))
                                        }
                                        setCurrentVehicle(vehicle)
                                    }}
                                    value={row}
                                />
                            </Grid>
                        </Grid>
                    );
                },
                sortValue: (row) => {
                    const vehicle = row?.vehicle;
                    return vehicle?.label
                }
            },
            {
                key: "lastMaintenanceDate",
                width: 150,
                sort: true,
                show: true,
                title: messages["serviceDetailView_lastPerformed"],
                export: true,
                render: (row) => {
                    let vehicle = vehicles.length ? vehicles.find((x) => x.id === row.unitId) : {}
                    if (!vehicle?.lastMaintenanceServiceUnitLog?.maintenanceDate) {
                        return <span />;
                    } else
                        return (
                            <span>
                                {moment(vehicle?.lastMaintenanceServiceUnitLog?.maintenanceDate).format(GetFormatForMoment(user.dateformat) + " h:mm a")}
                            </span>
                        );
                },
                sortValue: (row) => {
                    let vehicle = vehicles.length ? vehicles.find((x) => x.id === row.unitId) : {}
                    return vehicle?.lastMaintenanceServiceUnitLog?.maintenanceDate
                }
            },
            {
                key: "miles",
                width: 150,
                sort: true,
                show: true,
                title: user.isKph ? messages['vehicleDetailView_km'] : messages["vehicleDetailView_miles"],
                export: true,
                render: (row) => {
                    if (!serviceDetail.miles) return <span />;
                    const vehicle = row?.vehicle
                    return (
                        <>
                            {getMaintenanceBar("odometer", row.status, user.isKph ? convertToKPH(row.lastMaintenanceOdometer) : row.lastMaintenanceOdometer, user.isKph ? convertToKPH(row.nextServiceOdometer) : row.nextServiceOdometer, user.isKph ? convertToKPH(vehicle?.displayOdometer || 0) : vehicle?.displayOdometer || 0, messages)}
                        </>
                    );
                },
                sortValue: (row) => {
                    if (!serviceDetail.miles || !row) {
                        return "";
                    }

                    const vehicle = row?.vehicle

                    const percentage = getMaintenanceValue("odometer", row.lastMaintenanceOdometer, row.nextServiceOdometer, vehicle?.displayOdometer || 0)
                    return percentage;
                }
            },
            {
                key: "days",
                width: 150,
                sort: true,
                show: true,
                title: messages["vehicleDetailView_days"],
                export: true,
                render: (row) => {
                    if (!serviceDetail.days) return <span />;
                    return (
                        <>
                            {getMaintenanceBar("time", row?.status, row?.lastMaintenanceDate, row?.nextServiceDate, moment(), messages)}
                        </>
                    );
                },
                sortValue: (row) => {
                    if (!serviceDetail.days || !row) {
                        return "";
                    }

                    const percentage = getMaintenanceValue("time", row.lastMaintenanceDate, row.nextServiceDate, moment())
                    return percentage;
                }
            },
            {
                key: "hours",
                width: 150,
                sort: true,
                show: true,
                title: messages["engineHours"],
                export: true,
                render: (row) => {
                    if (!serviceDetail.engineHours) return <span />;
                    const vehicle = row?.vehicle
                    return (
                        <>
                            {getMaintenanceBar("engineHours", row.status, row.lastMaintenanceEngineHours, row.nextServiceEngineHours, vehicle?.displayHours || 0, messages)}
                        </>
                    );
                },
                sortValue: (row) => {
                    if (!serviceDetail.engineHours || !row) {
                        return "";
                    }

                    const vehicle = row?.vehicle

                    const percentage = getMaintenanceValue("engineHours", row.lastMaintenanceEngineHours, row.nextServiceEngineHours, vehicle?.displayHours || 0)
                    return percentage;
                }
            },
            {
                key: "estimatedCost",
                width: 100,
                sort: true,
                show: true,
                title: messages["vehicleDetailView_cost"],
                export: true,
                render: (row) => {
                    let cost = row ? row.estimatedCost : 0;
                    return (
                        <FormattedNumber value={cost} style="currency" currency="USD" />
                    );
                },
                sortValue: (row) => {
                    return row ? Math.floor(row.estimatedCost) : 0
                }
            },
            {
                key: "status",
                width: 150,
                sort: true,
                show: true,
                title: messages["vehicleDetailView_reminders"],
                export: true,
                render: (row) => {
                    if (row.status === null || row.status === undefined) {
                        return <span />;
                    }
                    let color = "";
                    let tooltip = "";
                    if (row.status === "PastDue") {
                        color = styles.purple;
                        tooltip = messages["pastDue"];
                    } else if (row.status === "Due") {
                        color = styles.red;
                        tooltip = messages["due"];
                    } else if (
                        row.status === "DueNow" ||
                        row.status === "DueSoon"
                    ) {
                        color = styles.orange;
                        tooltip = messages["dueSoon"];
                    } else if (row.status === "Scheduled") {
                        color = styles.green;
                        tooltip = messages["scheduled"];
                    } else if (row.status === "Done") {
                        color = styles.black;
                        tooltip = messages["done"];
                    }
                    return (
                        <Pill
                            style={{ width: 110 }}
                            item={{ label: tooltip, color: color }}
                        />
                    );
                },
                sortValue: (row) => {
                    return row?.status
                },
            },
            {
                key: "subscribersCount",
                width: 80,
                sort: true,
                show: true,
                title: messages["vehicleDetailView_subscribers"],
                export: true,
                render: (row) => {
                    const vehicle = row?.vehicle
                    return (
                        <>
                            {row.subscribersCount > 0 && (
                                <Icons
                                    style={{
                                        color: "#000000",
                                        cursor: "pointer",
                                        fontSize: 20,
                                    }}
                                    onClick={() => handleDetails(row.id, "MaintenanceServiceUnit", 0, vehicle.Label)}
                                    className={
                                        loadingServiceDetail ? "actions-disabled" : ""
                                    }
                                    name="people"
                                />
                            )}
                        </>
                    );
                },
                sortValue: (row) => {
                    return row?.subscribersCount || 0
                },
            },
            {
                key: "notes",
                width: 60,
                sort: true,
                show: true,
                title: messages["vehicleDetailView_notes"],
                export: true,
                render: (row) => {
                    const vehicle = row?.vehicle
                    return (
                        <div>
                            {row && row.notes && (
                                <Icons
                                    style={{
                                        color: "#000000",
                                        cursor: "pointer",
                                        fontSize: 20,
                                    }}
                                    onClick={() => handleDetails(row.id, "MaintenanceServiceUnit", 1, vehicle.Label)
                                    }
                                    className={
                                        loadingServiceDetail || loadingVehicles ? "actions-disabled" : ""
                                    }
                                    name="comment"
                                />
                            )}
                        </div>
                    );
                },
            },
            {
                key: "attachments",
                width: 100,
                sort: true,
                show: true,
                title: messages["vehicleDetailView_attachments"],
                export: true,
                render: (row) => {
                    const vehicle = row?.vehicle
                    if (!row.fileIds) {
                        return <span />;
                    }
                    return (
                        <div>
                            {row && row.fileIds && row.fileIds.length > 0 && (
                                <Icons
                                    style={{
                                        color: "#000000",
                                        cursor: "pointer",
                                        fontSize: 20,
                                    }}
                                    className={
                                        loadingServiceDetail || loadingVehicles ? "actions-disabled" : ""
                                    }
                                    onClick={() => handleDetails(row.id, "MaintenanceServiceUnit", 2, vehicle.Label)
                                    }
                                    name="attach_file"
                                />
                            )}
                        </div>
                    );
                },
                sortValue: (row) => {
                    return row?.fileIds?.length || 0
                }
            },
        ])
    }, [serviceDetail, loadingServiceDetail, loadingVehicles, locale, vehicleList, user])

    useEffect(() => {
        if (serviceDetail.units && data.length && !loadingServiceDetail && !loadingVehicles) {
            const list = data && serviceDetail.units && data.filter((x) => serviceDetail.units.map((u) => u.unitId.toString() === x.id.toString()));
            setVehicleList(list);
        }

    }, [data, serviceDetail, loadingServiceDetail, loadingVehicles])

    useEffect(() => {
        if (vehicleList.length) {
            let currentData = serviceDetail.units?.filter((x) => x.status != "Done" && vehicleList.filter((vehicle) => vehicle.id == x.unitId).length > 0) || []

            currentData = currentData.map((item) => {
                const vehicle = vehicleList.find((unit) => unit.id && unit.id.toString() === item?.unitId.toString())
                return { ...item, vehicle }
            })
            tableRef?.current?.setSort && tableRef?.current?.setSort({ key: 'status', order: 'desc' })
            pagination.onChangeData(currentData, { key: 'status', order: 'desc' })
            if (currentVehicle) {
                const vehicle = getVehicleById(currentVehicle.id);
                setCurrentVehicle(vehicle);
            }
        }
    }, [vehicleList])

    const getDataToExport = () => {
        let values = []
        let allData = pagination?.allData

        if (allData) {
            allData.forEach((item) => {
                const vehicle = item?.vehicle;
                let vehicleData = vehicles.length ? vehicles.find((x) => x.id === item.unitId) : {}

                let date = ""
                if (vehicleData?.lastMaintenanceServiceUnitLog?.maintenanceDate) {
                    date = moment(vehicleData?.lastMaintenanceServiceUnitLog?.maintenanceDate).format(GetFormatForMoment(user.dateformat) + " h:mm a")
                }

                let miles = "";
                if (serviceDetail.miles) {
                    var expresionMiles = /\(0%\)/;
                    miles = getMaintenanceLabel("odometer", item.lastMaintenanceOdometer, item.nextServiceOdometer, vehicle?.displayOdometer || 0, messages);
                    if (expresionMiles.test(miles)) miles = ""
                }

                let days = "";
                if (serviceDetail.days) {
                    days = getMaintenanceLabel("time", item?.lastMaintenanceDate, item?.nextServiceDate, moment(), messages);
                }

                let hours = "";
                if (serviceDetail.engineHours) {
                    hours = getMaintenanceLabel("engineHours", item.lastMaintenanceEngineHours, item.nextServiceEngineHours, vehicle?.displayHours || 0, messages);
                }
                let estimatedCost = formatNumber(item.estimatedCost || 0, { style: "currency", currency: "USD" }).replace(/,/g, '.');
                let status = item?.status
                let subscribersCount = item?.subscribersCount || "";
                let notes = item.notes || ""
                let fileIds = item.fileIds.length || ""
                values.push([vehicle.label, date, miles, days, hours, estimatedCost, status, subscribersCount, notes, fileIds])
            })
        }
        return values
    }

    return (
        <Container className="container-view">
            <div className="service-detail-breadcrumb-container">
                <Breadcrumb
                    links={[
                        {
                            title: "Services",
                            link: "/app/maintenance/main/1",
                        },
                        {
                            title: serviceDetail?.title,
                        },
                    ]}
                    navigate={navigate}
                />
            </div>
            <Grid
                container
                direction="row"
                justifyContent="space-between"
                alignItems="center"
                style={{ padding: 15 }}
            >
                <Grid item xs={6} sm={2}>
                    <Grid
                        container
                        direction="row"
                        justifyContent="flex-start"
                        alignItems="center"
                        spacing={1}
                    >
                        <Grid item style={{ display: 'flex' }}>
                            <IconButton
                                tooltip={messages["add"]}
                                placement="bottom"
                                variant="secondary"
                                icon="add_box"
                                size="small"
                                isDisabled={loadingServiceDetail || loadingVehicles}
                                onClick={() => {
                                    dispatch(openModalScheduleMaintenanceServiceUnit({ open: true }))
                                }}
                            />
                            <TableButtons
                                tableRef={tableRef}
                                buttons={["export"]}
                                onExport={(type) => {
                                    if (type === "Csv") tableRef.current?.exportCSV()
                                    else tableRef.current?.exportPDF()
                                }}
                            />
                        </Grid>
                    </Grid>
                </Grid>
                <Grid item xs={12} sm={4} className="mb-10 text-right">
                    <SearchInput
                        placeholder={messages['search']}
                        searchText={pagination.searchText}
                        onSearchText={pagination.onSearchChange}
                        isDisabled={loadingServiceDetail || loadingVehicles}
                    />
                </Grid>
                <TableView
                    ref={tableRef}
                    {...pagination}
                    loading={loadingServiceDetail || loadingVehicles}
                    hideDragable
                    keyColumn="unitId"
                    hideAllInPagination
                    disableItemSelection
                    topheight={220}
                    onChangeDisplayData={(displayData) => changeIdsCallback(displayData, "unitId")}
                    getDataToExport={(data) => getDataToExport(data)}
                    exportName="service-detail"
                    exportOrientation="landscape"
                />
            </Grid>
            {/* Views */}
            {openDeleteMaintenanceServiceUnit && <DeleteMaintenanceService refresh={refreshTable} />}
            {openScheduleMaintenanceService && <ScheduleMaintenanceService service={serviceDetail} refresh={refreshTable} />}
            {openCompleteMaintenanceService && <CompleteMaintenanceService vehicle={currentVehicle} refresh={refreshTable} />}
            {openEntityDetails && <EntityDetails refresh={refreshTable} />}
        </Container>
    )
}

export default ServiceDetailView